import { SearchOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Checkbox,
  Flex,
  Input,
  Space,
  Table,
  Typography,
  theme,
} from "antd";
import type { IUkhoWorkRow } from "interfaces";
import React, { useState } from "react";
import { apiErrorMessage, useLeftNotParked } from "./api";
import { handledChipText, UkhoDialogTarget } from "./lib";
import { ParkWithDateModal } from "./ParkWithDateModal";
import { useDebouncedValue } from "./useDebouncedValue";
import {
  buildWorkTableColumns,
  greyHandledRow,
  WrappingTag,
} from "./workTableColumns";

const EMPTY_TEXT = "Nothing waiting. Every ship that left per UKHO is parked.";

interface LeftNotParkedTabProps {
  /** Refreshes the live counts on the tab labels. */
  refetchCounts: () => void;
}

/**
 * Spec 11.2 "Left but not parked": ships whose latest AVCS-moving UKHO notice
 * is a leaving one and which eMariner still shows active. Oldest first (the
 * API's order); "show handled" adds this year's parked ones, greyed.
 */
export const LeftNotParkedTab: React.FC<LeftNotParkedTabProps> = ({
  refetchCounts,
}) => {
  const { token } = theme.useToken();
  const [searchText, setSearchText] = useState("");
  const [showHandled, setShowHandled] = useState(false);
  const [parkTarget, setParkTarget] = useState<UkhoDialogTarget | null>(null);
  const search = useDebouncedValue(searchText);
  const { data, isLoading, isFetching, isError, error, refetch } =
    useLeftNotParked(showHandled, search);

  const handleParked = () => {
    setParkTarget(null);
    refetch();
    refetchCounts();
  };

  const columns = buildWorkTableColumns({
    dayColumnTitle: "Left per UKHO",
    today: new Date(),
    renderCustomer: (row) => row.customer ?? "",
    renderAction: (row: IUkhoWorkRow) =>
      row.handled ? (
        <WrappingTag color="green">
          {handledChipText("parked", row.handled)}
        </WrappingTag>
      ) : (
        <Button size="small" type="primary" onClick={() => setParkTarget(row)}>
          Park with this date
        </Button>
      ),
  });

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Typography.Text type="secondary">
        UKHO moved the charts away; eMariner still shows the ship active
      </Typography.Text>
      <Flex justify="space-between" align="center" gap="middle" wrap>
        <Input
          placeholder="Search ship, IMO or customer"
          prefix={<SearchOutlined />}
          allowClear
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          style={{ width: 260 }}
        />
        <Checkbox
          checked={showHandled}
          onChange={(event) => setShowHandled(event.target.checked)}
        >
          show handled
        </Checkbox>
      </Flex>
      {isError ? (
        <Alert type="error" showIcon message={apiErrorMessage(error)} />
      ) : (
        <Table<IUkhoWorkRow>
          rowKey={(row) => `${row.vesselId}-${row.transferId}`}
          columns={columns}
          dataSource={data}
          loading={isLoading || isFetching}
          onRow={(row) => greyHandledRow(row, token.colorTextTertiary)}
          locale={search.trim() ? undefined : { emptyText: EMPTY_TEXT }}
          pagination={{ defaultPageSize: 50, hideOnSinglePage: true }}
        />
      )}
      {parkTarget && (
        <ParkWithDateModal
          open
          target={parkTarget}
          onClose={() => setParkTarget(null)}
          onDone={handleParked}
        />
      )}
    </Space>
  );
};
